import {
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Save } from 'lucide-react';
import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';

import DayTimeSlot from '../../components/settings/WorkingHours/DayTimeSlot';
import Notifications from '../../components/common/Notifications';
import { useWorkingHours } from '../../hooks/workingHours/useWorkingHours';
import { useWorkingHoursActions } from '../../hooks/workingHours/useWorkingActionHours';
import { convertTo12HourFormat } from '../../utils/timeUtils';
import { DAYS_OF_WEEK, TimeSlot } from '../../types/workingHours';
import { NotificationT, SEVERITY } from '../../types/notification';
import { TIMEZONES } from '../../types/timezone';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { organizationEndpoints } from '../../services/api/endpoints/organisation.endpoint';
import { fetchOrganiationsById } from '../../store/slices/organizationSlice';

export interface WorkingHoursSettingsRef {
  save: () => Promise<void>;
}

interface WorkingHoursSettingsProps {
  hideSaveButton?: boolean;
}

const WorkingHoursSettings = forwardRef<WorkingHoursSettingsRef, WorkingHoursSettingsProps>(
  ({ hideSaveButton = false }, ref) => {
    const dispatch = useDispatch<AppDispatch>();
    const organization = useSelector(
      (state: RootState) => state.organizations.selectedOrganisation
    );

    const [workingHours, setWorkingHours] = useState<Record<string, TimeSlot[]>>();
    const [notification, setNotification] = useState<NotificationT>({
      open: false,
      message: '',
      severity: SEVERITY.SUCCESS,
    });

    const { control, getValues } = useForm({
      defaultValues: {
        isChatDisabled: organization?.isChatDisabled,
        companyName: organization?.companyName,
        chatDescription: organization?.chatDescription,
        resourceProfessional: organization?.resourceProfessional,
        company: organization?.company,
        client: organization?.client,
        planType: organization?.planType,
        isBookingEnabled: organization?.isBookingEnabled,
        bookingDescription: organization?.bookingDescription,
        clientTableName: organization?.clientTableName,
        ingest_status: organization?.ingest_status,
        chatPrefills: organization?.chatPrefills || [],
        isVerifyBySMS: organization?.isVerifyBySMS || false,
        cutoffHours: organization?.company.cutoffHours || '24',
        timezone: organization?.company.timezone || 'Australia/Sydney',
      },
    });

    const { settingsOrgWorkingHours, refetch, isLoading } = useWorkingHours();
    const { createWorkingHours, deleteWorkingHour, updateWorkingHours } = useWorkingHoursActions();

    const showNotification = useCallback((message: string, severity: NotificationT['severity']) => {
      setNotification({ open: true, message, severity });
    }, []);

    const handleTimeChange = (day: string, index: number, updates: Partial<TimeSlot>) => {
      setWorkingHours(prevHours => {
        const newState = {
          ...prevHours,
          [day]: (prevHours?.[day] ?? []).map((slot, i) =>
            i === index
              ? { ...slot, ...updates, isModified: slot.id ? true : slot.isModified }
              : slot
          ),
        };

        if (index >= newState[day].length) {
          newState[day].push({
            startTime: updates.startTime || '',
            endTime: updates.endTime || '',
            enabled: updates.enabled,
            isModified: updates.isModified || false,
            id: undefined,
          });
        }

        return newState;
      });
    };

    const handleToggleDay = (day: string, enabled: boolean) => {
      setWorkingHours(prev => {
        if (!prev) return prev;

        const updatedDay = enabled
          ? [{ startTime: '09:00', endTime: '17:00', enabled: true, isModified: false }]
          : (prev[day] || []).map(slot => ({ ...slot, enabled: false, isModified: true }));

        return { ...prev, [day]: updatedDay };
      });
    };

    const processWorkingHours = async (
      days: [string, TimeSlot[]][],
      action: 'create' | 'update' | 'delete'
    ) => {
      if (days.length === 0) return;

      try {
        const formattedDays = days.flatMap(([day, slots]) =>
          slots.map(slot => ({
            id: slot.id,
            day,
            startTime: convertTo12HourFormat(slot.startTime),
            endTime: convertTo12HourFormat(slot.endTime),
            enabled: slot.enabled,
            orgId: '',
            isModified: slot.isModified,
          }))
        );

        switch (action) {
          case 'create':
            await createWorkingHours(formattedDays.filter(slot => slot.id === undefined));
            showNotification('Working hours created successfully', SEVERITY.SUCCESS);
            break;
          case 'update':
            await updateWorkingHours(formattedDays.filter(slot => slot.isModified && slot.id));
            showNotification('Working hours updated successfully', SEVERITY.SUCCESS);
            break;
          case 'delete':
            const ids = formattedDays
              .filter(slot => !slot.enabled && slot.isModified)
              .map(slot => slot.id)
              .filter((id): id is number => id !== undefined);
            await deleteWorkingHour(ids);
            showNotification('Working hours deleted successfully', SEVERITY.ERROR);
            break;
        }
      } catch (error) {
        showNotification(`Failed to ${action} working hours. Please try again.`, SEVERITY.ERROR);
        throw error;
      }
    };

    const handleSave = async () => {
      if (!organization) return;
      if (!workingHours) return;

      try {
        const formData = getValues();
        const updatedData = {
          ...formData,
        };
        await organizationEndpoints.updateOrganisation(organization.id, updatedData);
        await dispatch(fetchOrganiationsById(organization?.id));

        setNotification({
          open: true,
          message: 'Settings updated successfully',
          severity: SEVERITY.SUCCESS,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update settings';
        setNotification({
          open: true,
          message,
          severity: SEVERITY.ERROR,
        });
      }

      try {
        const newDays = Object.entries(workingHours).filter(([_, slots]) =>
          slots.some(slot => slot.id === undefined && slot.enabled)
        );

        const updatedDays = Object.entries(workingHours).filter(([_, slots]) =>
          slots.some(slot => slot.isModified && slot.id && slot.enabled)
        );

        const deletedDays = Object.entries(workingHours).filter(([_, slots]) =>
          slots.some(slot => !slot.enabled && slot.id)
        );

        await Promise.all([
          processWorkingHours(newDays, 'create'),
          processWorkingHours(updatedDays, 'update'),
          processWorkingHours(deletedDays, 'delete'),
        ]);

        refetch();
      } catch (error) {
        console.error('Error saving working hours:', error);
      }
    };

    // Expose save function to parent components via ref
    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    useEffect(() => {
      if (settingsOrgWorkingHours) {
        const parsedWorkingHours: Record<string, TimeSlot[]> = Object.keys(
          settingsOrgWorkingHours
        ).reduce(
          (acc, day) => {
            acc[day] = settingsOrgWorkingHours[day]
              .map((slot: any) => ({
                startTime: slot.startTime,
                endTime: slot.endTime,
                enabled: slot.enabled,
                id: slot.id,
                isModified: slot.isModified || false,
              }))
              .sort((a: any, b: any) => (a.startTime > b.startTime ? 1 : -1));
            return acc;
          },
          {} as Record<string, TimeSlot[]>
        );
        setWorkingHours(parsedWorkingHours);
      }
    }, [settingsOrgWorkingHours]);

    if (isLoading || !workingHours) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          backgroundColor: 'theme.palette.background.paper',
          borderRadius: { xs: 2, sm: 1 },
          border: '1px solid theme.palette.divider',
          mx: { xs: 1, sm: 0 },
          mt: { xs: 2, sm: 10 },
          '&:hover': {
            borderColor: 'theme.palette.primary.main',
            transition: 'border-color 0.3s ease',
          },
        }}
        className="w-full xl:max-w-4xl mx-auto"
      >
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5 }}>
              Working Hours & Timezone
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure your organization's working days, hours, timezone and booking cutoff time.
            </Typography>
          </Box>
          {!hideSaveButton && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<Save size={16} />}
                onClick={handleSave}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: '#5C6BC0',
                  color: '#ffffff',
                  boxShadow: '0 4px 14px 0 rgba(92, 107, 192, 0.39)',
                  '&:hover': {
                    backgroundColor: '#3F51B5',
                    boxShadow: '0 6px 20px 0 rgba(92, 107, 192, 0.5)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Save Changes
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="company.timezone"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={TIMEZONES}
                  value={field.value}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Timezone"
                      helperText="Select the timezone for your organization"
                    />
                  )}
                />
              )}
            />
            <Controller
              name="company.cutoffHours"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="How early can you cancel or reschedule?"
                  type="number"
                  fullWidth
                  defaultValue="24"
                  helperText="Enter how many hours before your booking you'd like to allow changes."
                />
              )}
            />
          </div>
        </Box>

        <Box sx={{ mt: 3 }} className="working-hours-tour">
          {DAYS_OF_WEEK.map(day => (
            <DayTimeSlot
              key={day}
              day={day}
              timeSlot={workingHours[day]?.filter(slot => slot.enabled) || []}
              onChange={handleTimeChange}
              onDeleteSlot={(day, index) => {
                setWorkingHours(prev => ({
                  ...prev,
                  [day]: (prev?.[day] ?? []).map((slot, i) =>
                    i === index ? { ...slot, enabled: false, isModified: true } : slot
                  ),
                }));
              }}
              onToggleDay={handleToggleDay}
            />
          ))}
        </Box>

        <Notifications
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        />
      </Paper>
    );
  }
);

export default WorkingHoursSettings;
